import { ApiError, type AttachmentLimitsResponse, type AttachmentStatusItem, schemas } from "@/api";

export type AttachmentKind = "image" | "document";

/** Why a file wasn't attached: found before uploading, or reported by the upload (413/415/400). */
export type AttachmentRejection =
  | { reason: "tooLarge"; name: string; kind: AttachmentKind; size: number; maxSize: number }
  | { reason: "unsupported"; name: string }
  | { reason: "tooMany"; max: number };

const KINDS: AttachmentKind[] = ["image", "document"];
const GENERIC_TYPE = "application/octet-stream";

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot).toLowerCase() : "";
};

/**
 * Image or document, or null when the backend wouldn't accept it. Mirrors
 * `validate_attachment_file`: the browser's type decides, and only a missing or
 * generic type (common for .md and Office files) falls back to the extension.
 */
export function attachmentKind(file: Pick<File, "name" | "type">, limits: AttachmentLimitsResponse) {
  const byType = KINDS.find((kind) => limits[kind].mimeTypes.includes(file.type));
  if (byType || (file.type && file.type !== GENERIC_TYPE)) return byType ?? null;
  const ext = extensionOf(file.name);
  return KINDS.find((kind) => limits[kind].extensions.includes(ext)) ?? null;
}

/** Splits files into those to upload and those the backend would reject. No limits (yet): upload everything. */
export function checkAttachments(files: File[], limits: AttachmentLimitsResponse | undefined) {
  if (!limits) return { accepted: files, rejected: [] as AttachmentRejection[] };
  const accepted: File[] = [];
  const rejected: AttachmentRejection[] = [];
  for (const file of files) {
    const kind = attachmentKind(file, limits);
    if (!kind) {
      rejected.push({ reason: "unsupported", name: file.name });
      continue;
    }
    const { maxSize } = limits[kind];
    if (file.size > maxSize) rejected.push({ reason: "tooLarge", name: file.name, kind, size: file.size, maxSize });
    else accepted.push(file);
  }
  return { accepted, rejected };
}

/** The file input's `accept`: every extension and type the backend takes. */
export function acceptAttribute(limits: AttachmentLimitsResponse | undefined) {
  if (!limits) return undefined;
  return KINDS.flatMap((kind) => [...limits[kind].extensions, ...limits[kind].mimeTypes]).join(",");
}

/** Uploads at most `size` files per request (the backend's `maxFiles`). */
export function batches<T>(items: T[], size: number | undefined): T[][] {
  if (!size || items.length <= size) return items.length ? [items] : [];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** The rejection behind a failed upload, when the backend said which rule a file broke. */
export function rejectionFromError(error: unknown, files: File[]): AttachmentRejection | null {
  if (!(error instanceof ApiError)) return null;
  const onlyName = files.length === 1 ? files[0]?.name : undefined;
  switch (error.code) {
    case "file_too_large": {
      const parsed = schemas.zFileTooLargeError.safeParse(error.body);
      const name = parsed.data?.detail.filename ?? onlyName;
      if (!parsed.success || !name) return null;
      const { file_kind: kind, size, max_size: maxSize } = parsed.data.detail;
      return { reason: "tooLarge", name, kind, size, maxSize };
    }
    case "unsupported_file_type": {
      const parsed = schemas.zUnsupportedFileTypeError.safeParse(error.body);
      const name = parsed.data?.detail.filename ?? onlyName;
      return name ? { reason: "unsupported", name } : null;
    }
    case "too_many_files": {
      const parsed = schemas.zTooManyFilesError.safeParse(error.body);
      return parsed.success ? { reason: "tooMany", max: parsed.data.detail.max } : null;
    }
    default:
      return null;
  }
}

/** Why a file can't be used, by the backend's stable reason code. */
const REASON_KEYS = {
  MALWARE_DETECTED: "reasons.malware",
  TYPE_MISMATCH: "reasons.type",
  TYPE_NOT_ALLOWED: "reasons.type",
  ENCRYPTED_DOCUMENT: "reasons.encrypted",
  MACRO_DOCUMENT: "reasons.macros",
  CORRUPT_FILE: "reasons.damaged",
  EMPTY_FILE: "reasons.damaged",
} as const;

/** The `chat:attachments.reasons.*` key explaining why a file failed its check. */
export const reasonKey = (status: Pick<AttachmentStatusItem, "reasonCode">) =>
  REASON_KEYS[(status.reasonCode ?? "") as keyof typeof REASON_KEYS] ?? "reasons.other";
